import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, getToken } from '../lib/api.js';
import { useAuth, reportApiError } from '../lib/store.js';
import { useInvalidate } from '../lib/queries.js';
import { Spinner, Switch } from './ui.js';
import { Logo } from './Logo.js';
import { useT } from '../lib/i18n.js';

/**
 * Der Assistent als Seitenleiste.
 *
 * Er redet nicht nur, er arbeitet: Jeder Werkzeugaufruf wird live angezeigt,
 * während er läuft. Und er führt durch die Einrichtung: Man kann ihm Bilder
 * hineinziehen (Screenshots, Logo), er schaut sie an, beschriftet sie und
 * speichert sie im Projekt. Wo der Nutzer selbst klicken muss (Social-Login),
 * gibt er einen Link, der direkt dorthin führt.
 */

interface Action { tool: string; summary: string; ok: boolean; running?: boolean }
interface Turn { role: 'user' | 'assistant'; text: string; actions: Action[]; images?: string[] }
interface Attachment { key: string; preview: string; name: string; assetId: string | null; error?: string }

const TOOL_LABEL: Record<string, string> = {
  list_projects: 'Projekte durchsehen',
  get_project: 'Projekt öffnen',
  get_setup_status: 'Einrichtung prüfen',
  describe_from_link: 'Link lesen',
  create_project: 'Projekt anlegen',
  update_project: 'Projekt ändern',
  save_images: 'Bilder speichern',
  list_recipes: 'Rezepte ansehen',
  update_recipe: 'Rezept einstellen',
  create_recipe: 'Rezept anlegen',
  set_schedule: 'Zeitplan setzen',
  set_autopilot: 'Automatik einstellen',
  estimate_cost: 'Kosten schätzen',
  start_agent_run: 'Posts bauen',
  list_posts: 'Beiträge durchsehen',
  update_post: 'Beitrag ändern',
  rewrite_caption: 'Caption neu schreiben',
  approve_posts: 'Beiträge freigeben',
  delete_posts: 'Beiträge verwerfen',
  get_analytics: 'Zahlen auswerten',
  get_account_status: 'Zugänge prüfen',
  search_music: 'Musik suchen',
};

const SUGGESTIONS_GLOBAL = [
  'Richte mit mir ein neues Projekt ein',
  'Was ist gerade offen?',
  'Wie funktioniert das hier?',
];
const SUGGESTIONS_PROJECT = [
  'Was fehlt noch bei der Einrichtung?',
  'Ich lade dir Screenshots und mein Logo hoch',
  'Bau mir 2 Posts',
  'Schreib die Caption vom letzten wartenden Beitrag frecher',
];

/** [Text](/pfad) als Knopf, **fett** als fett; alles andere bleibt Text. */
function RichText({ text, onLink }: { text: string; onLink: (path: string) => void }) {
  const parts: ReactNode[] = [];
  const re = /\[([^\]]+)\]\((\/[^)\s]*)\)|\*\*([^*]+)\*\*/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let k = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[1] && m[2]) {
      const path = m[2];
      parts.push(
        <button key={k++} type="button" className="assistant-link" onClick={() => onLink(path)}>
          {m[1]} →
        </button>,
      );
    } else if (m[3]) {
      parts.push(<strong key={k++}>{m[3]}</strong>);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

export function AssistantPanel({
  open, onClose, projectId, projectName, seed,
}: {
  open: boolean;
  onClose: () => void;
  projectId: string | null;
  projectName?: string;
  /** Eine Nachricht, die beim Öffnen sofort gesendet wird (z. B. „Richte mit mir ein Projekt ein") */
  seed?: { message: string; at: number } | null;
}) {
  const t = useT();
  const navigate = useNavigate();
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [allowPublish, setAllowPublish] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [dragging, setDragging] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const seenSeed = useRef<number>(0);
  const invalidate = useInvalidate();
  const toast = useAuth((s) => s.toast);
  const refreshOrg = useAuth((s) => s.refreshOrg);

  const uploading = attachments.some((a) => !a.assetId && !a.error);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 60);
  }, [open]);

  // Escape schließt wie bei jedem anderen Dialog
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [turns, busy]);

  // Mit einer vorbereiteten Nachricht geöffnet: sofort losschicken.
  useEffect(() => {
    if (open && seed && seed.at !== seenSeed.current && !busy) {
      seenSeed.current = seed.at;
      void send(seed.message);
    }
  }, [open, seed]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Bilder hochladen, sobald sie ausgewählt, hineingezogen oder eingefügt werden. */
  async function addFiles(files: File[]) {
    const images = files.filter((f) => f.type.startsWith('image/')).slice(0, 8 - attachments.length);
    if (!images.length) {
      if (files.length) toast(t('Hier gehen nur Bilder (PNG, JPG, WebP).'), 'err');
      return;
    }
    const fresh = images.map((f) => ({
      key: `${f.name}-${f.size}-${Math.random().toString(36).slice(2, 7)}`,
      preview: URL.createObjectURL(f),
      name: f.name,
      assetId: null as string | null,
    }));
    setAttachments((a) => [...a, ...fresh]);
    await Promise.all(images.map(async (file, n) => {
      const key = fresh[n]!.key;
      try {
        const form = new FormData();
        form.append('file', file);
        if (projectId) form.append('projectId', projectId);
        const { asset } = await api.upload<{ asset: { id: string } }>('/assets/upload', form);
        setAttachments((a) => a.map((x) => (x.key === key ? { ...x, assetId: asset.id } : x)));
      } catch (err) {
        setAttachments((a) => a.map((x) => (x.key === key ? { ...x, error: t('Hochladen fehlgeschlagen') } : x)));
        reportApiError(err, t('Bild ließ sich nicht hochladen'));
      }
    }));
  }

  function removeAttachment(key: string) {
    setAttachments((a) => {
      const gone = a.find((x) => x.key === key);
      if (gone) URL.revokeObjectURL(gone.preview);
      return a.filter((x) => x.key !== key);
    });
  }

  function openLink(path: string) {
    navigate(path);
    // Auf dem Handy deckt die Leiste die Seite ab; dort schließen, damit man sieht, wohin es ging.
    if (window.matchMedia('(max-width: 900px)').matches) onClose();
  }

  async function send(raw: string) {
    const ready = attachments.filter((a) => a.assetId);
    const text = raw.trim() || (ready.length ? t('Hier sind meine Bilder.') : '');
    if (!text || busy || uploading) return;

    const images = ready.map((a) => a.preview);
    const ids = ready.map((a) => a.assetId!) ;
    setTurns((tt) => [...tt, { role: 'user', text, actions: [], images }, { role: 'assistant', text: '', actions: [] }]);
    setInput('');
    setAttachments([]);
    setBusy(true);

    const patchLast = (fn: (turn: Turn) => Turn) =>
      setTurns((tt) => tt.map((turn, i) => (i === tt.length - 1 ? fn(turn) : turn)));

    try {
      const res = await fetch('/api/assistant/chat', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ message: text, conversationId, projectId, allowPublish, attachments: ids }),
      });

      if (!res.ok || !res.body) {
        const err = await res.json().catch(() => ({ error: t('Der Assistent ist nicht erreichbar.') }));
        patchLast((turn) => ({ ...turn, text: (err as { error: string }).error }));
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const chunks = buffer.split('\n\n');
        buffer = chunks.pop() ?? '';

        for (const chunk of chunks) {
          const line = chunk.split('\n').find((l) => l.startsWith('data: '));
          if (!line) continue;

          const event = JSON.parse(line.slice(6)) as {
            type: string; text?: string; tool?: string; summary?: string;
            ok?: boolean; credits?: number; conversationId?: string;
          };

          if (event.type === 'text' && event.text) {
            patchLast((turn) => ({ ...turn, text: event.text! }));
          }
          if (event.type === 'tool_start' && event.tool) {
            patchLast((turn) => ({
              ...turn,
              actions: [...turn.actions, { tool: event.tool!, summary: '', ok: true, running: true }],
            }));
          }
          if (event.type === 'tool_end' && event.tool) {
            patchLast((turn) => ({
              ...turn,
              actions: turn.actions.map((a) =>
                a.tool === event.tool && a.running
                  ? { tool: a.tool, summary: event.summary ?? '', ok: event.ok ?? false }
                  : a,
              ),
            }));
            // Der Assistent hat etwas verändert, Ansicht nachziehen
            invalidate.all();
          }
          if (event.type === 'done') {
            if (event.conversationId) setConversationId(event.conversationId);
            void refreshOrg();
          }
          if (event.type === 'error') {
            patchLast((turn) => ({ ...turn, text: event.text ?? t('Da ist etwas schiefgegangen.') }));
            toast(event.text ?? t('Assistent-Fehler'), 'err');
          }
        }
      }
    } catch (err) {
      patchLast((turn) => ({ ...turn, text: t('Verbindung abgebrochen: {err}', { err: String(err) }) }));
    } finally {
      setBusy(false);
    }
  }

  function newChat() {
    setTurns([]);
    setConversationId(null);
    setAttachments([]);
  }

  if (!open) return null;

  const suggestions = projectId ? SUGGESTIONS_PROJECT : SUGGESTIONS_GLOBAL;

  return (
    <>
      <div className="assistant-scrim" onClick={onClose} />
      <aside
        className={`assistant pop ${dragging ? 'assistant-drop' : ''}`}
        aria-label={t('Assistent')}
        onDragOver={(e) => { if (e.dataTransfer.types.includes('Files')) { e.preventDefault(); setDragging(true); } }}
        onDragLeave={(e) => { if (e.currentTarget === e.target) setDragging(false); }}
        onDrop={(e) => { e.preventDefault(); setDragging(false); void addFiles([...e.dataTransfer.files]); }}
      >
        <div className="assistant-head">
          <Logo size={30} radius={10} />
          <div className="grow">
            <div style={{ fontWeight: 600, fontSize: 14.5 }}>{t('Assistent')}</div>
            <div className="dim" style={{ fontSize: 12 }}>
              {projectName ? t('arbeitet an {name}', { name: projectName }) : t('übergreifend')}
            </div>
          </div>
          {turns.length > 0 && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={newChat} disabled={busy}>{t('Neu')}</button>
          )}
          <button type="button" className="btn btn-ghost btn-sm assistant-close" onClick={onClose} aria-label={t('Schließen')}>✕</button>
        </div>

        <div ref={scrollRef} className="assistant-body">
          {turns.length === 0 && (
            <div className="col" style={{ gap: 14, paddingTop: 8 }}>
              <div style={{ fontSize: 14, lineHeight: 1.6 }}>
                {t('Sag einfach, was passieren soll. Ich richte Projekte mit dir ein, nehme Screenshots und dein Logo entgegen, stelle Rezepte ein, baue Beiträge und gebe sie frei. Du schaust zu.')}
              </div>
              <div className="col" style={{ gap: 8 }}>
                {suggestions.map((s) => (
                  <button type="button" key={s} onClick={() => void send(t(s))} className="assistant-suggest">
                    {t(s)}
                  </button>
                ))}
              </div>
              <div className="dim" style={{ fontSize: 12.5 }}>{t('Bilder kannst du einfach hier hineinziehen oder einfügen.')}</div>
            </div>
          )}

          {turns.map((turn, i) => (
            <div key={i} className="col" style={{ gap: 8 }}>
              {turn.role === 'user' ? (
                <>
                  {!!turn.images?.length && (
                    <div className="assistant-thumbs assistant-thumbs-sent">
                      {turn.images.map((src) => <img key={src} src={src} alt="" />)}
                    </div>
                  )}
                  <div className="assistant-bubble">{turn.text}</div>
                </>
              ) : (
                <>
                  {turn.actions.map((a, k) => (
                    <div key={k} className="assistant-action">
                      {a.running ? (
                        <Spinner size={12} />
                      ) : (
                        <span style={{ color: a.ok ? 'var(--ok)' : 'var(--err)' }}>{a.ok ? '✓' : '✕'}</span>
                      )}
                      <span style={{ fontWeight: 600 }}>{t(TOOL_LABEL[a.tool] ?? a.tool)}</span>
                      {a.summary && <span className="dim truncate" style={{ flex: 1 }}>{a.summary}</span>}
                    </div>
                  ))}
                  {turn.text ? (
                    <div style={{ fontSize: 14, lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>
                      <RichText text={turn.text} onLink={openLink} />
                    </div>
                  ) : (
                    busy && i === turns.length - 1 && !turn.actions.length && (
                      <div className="row dim" style={{ gap: 9, fontSize: 13.5 }}>
                        <Spinner size={13} /> {t('denkt nach…')}
                      </div>
                    )
                  )}
                </>
              )}
            </div>
          ))}
        </div>

        <div className="assistant-foot">
          <div className="row" style={{ gap: 10, marginBottom: 10 }}>
            <Switch on={allowPublish} onChange={setAllowPublish} label={t('Veröffentlichen erlauben')} />
            <div className="grow" style={{ fontSize: 12.5, lineHeight: 1.4 }}>
              <span style={{ fontWeight: 600 }}>{t('Veröffentlichen erlauben')}</span>
              <div className="dim">
                {allowPublish
                  ? t('Der Assistent darf Beiträge live schalten.')
                  : t('Der Assistent baut nur. Jeder Beitrag wartet auf dein OK.')}
              </div>
            </div>
          </div>

          {attachments.length > 0 && (
            <div className="assistant-thumbs">
              {attachments.map((a) => (
                <div key={a.key} className={`assistant-thumb ${a.error ? 'assistant-thumb-err' : ''}`} title={a.error ?? a.name}>
                  <img src={a.preview} alt={a.name} />
                  {!a.assetId && !a.error && <span className="assistant-thumb-busy"><Spinner size={12} /></span>}
                  <button type="button" className="assistant-thumb-x" onClick={() => removeAttachment(a.key)} aria-label={t('Entfernen')}>✕</button>
                </div>
              ))}
            </div>
          )}

          <div className="row" style={{ gap: 8, alignItems: 'flex-end' }}>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              multiple
              hidden
              onChange={(e) => { void addFiles([...(e.target.files ?? [])]); e.target.value = ''; }}
            />
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => fileRef.current?.click()}
              disabled={busy || attachments.length >= 8}
              style={{ height: 46, minWidth: 46 }}
              aria-label={t('Bilder anhängen')}
              title={t('Bilder anhängen')}
            >
              📎
            </button>
            <textarea
              ref={inputRef}
              className="textarea grow"
              rows={2}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onPaste={(e) => {
                const files = [...e.clipboardData.files].filter((f) => f.type.startsWith('image/'));
                if (files.length) { e.preventDefault(); void addFiles(files); }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(input); }
              }}
              placeholder={t('Was soll ich tun?')}
              style={{ minHeight: 46, resize: 'none' }}
              disabled={busy}
            />
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void send(input)}
              disabled={busy || uploading || (!input.trim() && !attachments.some((a) => a.assetId))}
              style={{ height: 46, minWidth: 46 }}
              aria-label={t('Senden')}
            >
              {busy || uploading ? <Spinner size={14} /> : '↑'}
            </button>
          </div>
        </div>
        {dragging && <div className="assistant-dropzone">{t('Bilder hier loslassen')}</div>}
      </aside>
    </>
  );
}
